import { useState } from "react";
import {
  Package,
  PackageCheck,
  Truck,
  AlertCircle,
  Clock,
  MapPin,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import type {
  TrackingAddress,
  TrackingResult,
  TrackingStatus,
} from "@shared-core/data/api/tracking-types";
import {
  formatDateOrDash,
  formatDateTimeOrDash,
} from "@shared-core/domain/formatters";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../primitives/card";

export const STATUS_LABEL: Record<TrackingStatus, string> = {
  in_transit: "In transit",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
  attempted_delivery: "Delivery attempted",
  exception: "Exception",
  returning: "Returning to sender",
  unknown: "Unknown",
};

const STATUS_COLOR: Record<TrackingStatus, string> = {
  in_transit: "bg-blue-100 text-blue-700 ring-1 ring-blue-200",
  out_for_delivery: "bg-amber-100 text-amber-700 ring-1 ring-amber-200",
  delivered: "bg-green-100 text-green-700 ring-1 ring-green-200",
  attempted_delivery: "bg-orange-100 text-orange-700 ring-1 ring-orange-200",
  exception: "bg-red-100 text-red-700 ring-1 ring-red-200",
  returning: "bg-red-100 text-red-700 ring-1 ring-red-200",
  unknown: "bg-gray-100 text-gray-600 ring-1 ring-gray-200",
};

const STATUS_ICON: Record<TrackingStatus, typeof Truck> = {
  in_transit: Truck,
  out_for_delivery: Truck,
  delivered: PackageCheck,
  attempted_delivery: AlertCircle,
  exception: AlertCircle,
  returning: AlertCircle,
  unknown: Package,
};

export function StatusBadge({ status }: { status: TrackingStatus }) {
  const Icon = STATUS_ICON[status];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium ${STATUS_COLOR[status]}`}
    >
      <Icon className="size-3.5" />
      {STATUS_LABEL[status]}
    </span>
  );
}

function AddressBlock({ address }: { address: TrackingAddress }) {
  const lines = [
    address.name,
    address.company,
    address.line1,
    address.line2,
    address.town,
  ].filter(Boolean);
  const postcode = address.postcode;
  return (
    <div className="text-sm">
      {lines.map((line, i) => (
        <div key={i}>{line}</div>
      ))}
      {postcode && <div className="font-medium">{postcode}</div>}
    </div>
  );
}

export function TrackingResultCard({ result }: { result: TrackingResult }) {
  const [showAllEvents, setShowAllEvents] = useState(false);
  const visibleEvents = showAllEvents
    ? result.events
    : result.events.slice(0, 3);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base font-mono">
              {result.tracking_reference}
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-0.5 capitalize">
              {result.courier_code} parcel
            </p>
          </div>
          <StatusBadge status={result.current_status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {(result.delivery_address || result.service_name) && (
          <div className="rounded-md border border-border bg-muted/40 px-4 py-3 space-y-2">
            {result.delivery_address && (
              <div className="flex items-start gap-2">
                <MapPin className="size-3.5 shrink-0 mt-0.5 text-muted-foreground" />
                <AddressBlock address={result.delivery_address} />
              </div>
            )}
            {result.service_name && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Truck className="size-3.5 shrink-0" />
                <span>{result.service_name}</span>
              </div>
            )}
          </div>
        )}

        {result.current_status === "delivered" && result.proof_of_delivery ? (
          <div className="flex items-center gap-2 text-sm">
            <PackageCheck className="size-4 text-green-600 shrink-0" />
            <span>
              Delivered{" "}
              {formatDateTimeOrDash(result.proof_of_delivery.signed_at)}
              {result.proof_of_delivery.signatory && (
                <span className="text-muted-foreground">
                  {" "}
                  — signed by {result.proof_of_delivery.signatory}
                </span>
              )}
            </span>
          </div>
        ) : result.estimated_delivery ? (
          <div className="flex items-center gap-2 text-sm">
            <Clock className="size-4 text-muted-foreground shrink-0" />
            <span>Expected {formatDateOrDash(result.estimated_delivery)}</span>
          </div>
        ) : null}

        {result.events.length > 0 && (
          <div className="space-y-0">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
              Event history
            </p>
            <div className="space-y-0 border-l-2 border-border ml-1.5">
              {visibleEvents.map((event, i) => (
                <div key={i} className="relative pl-4 pb-3 last:pb-0">
                  <div className="absolute -left-[5px] top-1.5 size-2 rounded-full bg-border" />
                  <div className="flex items-start gap-2 text-sm">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium leading-snug">
                        {event.description}
                      </p>
                      {event.location && (
                        <p className="text-muted-foreground flex items-center gap-1 mt-0.5">
                          <MapPin className="size-3 shrink-0" />
                          {event.location}
                        </p>
                      )}
                    </div>
                    <time className="text-xs text-muted-foreground shrink-0 mt-0.5">
                      {formatDateTimeOrDash(event.timestamp)}
                    </time>
                  </div>
                </div>
              ))}
            </div>
            {result.events.length > 3 && (
              <button
                onClick={() => setShowAllEvents((v) => !v)}
                className="mt-2 text-sm text-primary hover:underline flex items-center gap-1"
              >
                {showAllEvents ? (
                  <>
                    <ChevronUp className="size-3.5" /> Show less
                  </>
                ) : (
                  <>
                    <ChevronDown className="size-3.5" /> Show{" "}
                    {result.events.length - 3} more events
                  </>
                )}
              </button>
            )}
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          Last checked {formatDateTimeOrDash(result.cached_at)}
        </p>
      </CardContent>
    </Card>
  );
}
